import { useMemo } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { isGuest } from "@common/models/Member.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import Shake from "@shakebugs/browser";

import { InterjectedRow, TableInterjections } from "./InterjectedRow.model";
import { useInterjectedRowDismissal } from "./useInterjectedRowDismissal";
import { useInterjectionCadence } from "./useInterjectionCadence";
import { useWhatsNewInterjection } from "./useWhatsNewInterjection";

export type InterjectedRowsModule = "feedback" | "crashes" | "users";

export interface UseTableInterjectionsOptions {
  hasActiveFilters: boolean;
  total?: number;
  /// ISO date strings for the currently loaded tickets, newest first - only meaningful (and only
  /// needed) for the feedback module, to position the what's-new row at its release date.
  ticketDates?: string[];
}

/**
 * The feedback module's "What's missing" / "Recommend us" row comes from useInterjectionCadence's
 * client-side-only trigger formula (there's no backend endpoint for placement/dismissal, and there
 * won't be). The "what's new" rows are different - one per unread changelog entry from the real changelog
 * API (useWhatsNewInterjection), feedback-only like the cadence row, and dismissing one also marks
 * its underlying changelog notification as read.
 */
export function useTableInterjections(
  module: InterjectedRowsModule,
  { hasActiveFilters, total, ticketDates }: UseTableInterjectionsOptions,
): TableInterjections {
  const { sendEvent } = useAnalytics();
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();
  const guest = isGuest(selectedWorkspace?.role);

  const cadenceRows = useInterjectionCadence(module === "feedback" && !guest ? selectedApp?.id : undefined, total ?? 0);
  const { rows: whatsNewRowsResult, markAsRead } = useWhatsNewInterjection(ticketDates ?? []);
  const whatsNewRows = useMemo(() => (module === "feedback" ? whatsNewRowsResult : []), [module, whatsNewRowsResult]);

  const rows: InterjectedRow[] = useMemo(() => [...whatsNewRows, ...cadenceRows], [whatsNewRows, cadenceRows]);

  const rowIds = useMemo(() => rows.map((row) => row.id), [rows]);
  const { dismissedIds, dismiss } = useInterjectedRowDismissal(rowIds);

  const visibleRows = useMemo(() => {
    if (hasActiveFilters || guest) return [];

    return rows.filter((row) => !dismissedIds.includes(row.id));
  }, [hasActiveFilters, guest, rows, dismissedIds]);

  return {
    rows: visibleRows,
    onDismiss: (id) => {
      const row = rows.find((candidate) => candidate.id === id);
      sendEvent("interjected_row_dismissed", { row_type: row?.type, row_id: id });
      dismiss(id);

      if (module === "feedback") markAsRead(id);
    },
    onFeedbackSubmit: (row, value) => {
      Shake.silentReport(value, { takeScreenshot: false, includeSessionReplay: false, showSuccessMessage: false });
    },
  };
}
